import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ItemStatus } from "../../../../../services/soap/item/responses/ListItemStatusResponse";
import { ListItemStatusRequest } from "../../../../../services/soap/item/requests/ListItemStatusRequest";

interface IItemStatusDropDownProps {
  selectedStatus: string;              // parent selected id
  onChange: (data: ItemStatus | null) => void; // null means cleared
  isEdit: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
}

const ItemStatusDropDown: React.FC<IItemStatusDropDownProps> = ({
  selectedStatus,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
}) => {
  const { store, itemService } = useDependency();
  const { t } = useTranslation("products");
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: "products" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<ItemStatus>({
    async load({ filterText }) {
      try {
        const request = new ListItemStatusRequest(store.Server, store.SessionId);
        const response = await itemService.listItemStatus(request);

        let items: ItemStatus[] = response?.ITEM_STATUS || [];
        if (!Array.isArray(items)) return { items: [] };

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
        }

        return { items };
      } catch (e) {
        console.error("Error fetching ItemStatus:", e);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE
  useEffect(() => {
    if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      const firstId = String((first as any).id);

      setSelectedKey(firstId);
      setInputValue(first.name ?? "");
      onChange(first);
    }
  }, [list.items, isEdit, onChange]);

  // ✅ sync from parent ONLY when user is not actively typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedStatus) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedStatus));
    const selected = list.items.find((x) => String((x as any).id) === String(selectedStatus));
    setInputValue(selected?.name ?? "");
  }, [selectedStatus, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ IMPORTANT: allow deleting text even if something is selected
    // If user input diverges from selected option label, clear the selectedKey
    if (selectedKey) {
      const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
      const selectedLabel = (selected?.name ?? "");
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ goal #4: when cleared -> empty selection
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText(""); // reset filtering
      onChange(null);         // tell parent it is cleared
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String((x as any).id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? ""); // show immediately
      onChange(selected);

      // clear filter so next focus shows all values
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={_t("status")}
      aria-label={_t("status")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={{ base: "100%", L: "50%" }}
      isDisabled={isEdit}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        // ✅ goal #2 show all values when user clicks in
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={(item as any).id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ItemStatusDropDown;
